import React, { useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { FilterBar, FilterState, ColumnDef, LEVEL_FIELDS } from './components/FilterBar';
import { ProductGrid } from './components/ProductGrid';
import { ChargeList } from './components/ChargeList';
import { ChargeDetail } from './components/ChargeDetail';
import { products, batches as initialBatches, batchDetails, Product, Batch, Portal } from './components/mockData';
import { TransferPage } from './components/TransferPage';
type Level = 1 | 2 | 3;
type View = 'main' | 'transfer';
const makeEmptyFilters = (level: Level): FilterState => ({
  search: '',
  dateFrom: '',
  dateTo: '',
  status: 'Alle',
  searchFields: LEVEL_FIELDS[level].map(f => f.key)
});
const CHARGE_COLUMNS: ColumnDef[] = [{
  key: 'batchNumber',
  label: 'Chargen-Nr.',
  required: true
}, {
  key: 'productionDate',
  label: 'Produktionsdatum'
}, {
  key: 'status',
  label: 'Status'
}];
const MATERIAL_COLUMNS: ColumnDef[] = [{
  key: 'name',
  label: 'Material',
  required: true
}, {
  key: 'batchNo',
  label: 'Chargen-Nr.'
}, {
  key: 'supplier',
  label: 'Lieferant'
}, {
  key: 'scan',
  label: 'Scan'
}, {
  key: 'mhd',
  label: 'MHD'
}, {
  key: 'we',
  label: 'Wareneingang'
}, {
  key: 'quantity',
  label: 'Menge'
}];
export function App() {
  const [level, setLevel] = useState<Level>(1);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  // Local mutable copy of batches so transfers persist within the session
  const [batches, setBatches] = useState<Batch[]>(initialBatches);
  const [view, setView] = useState<View>('main');
  const [returnToTransfer, setReturnToTransfer] = useState(false);
  // Per-level filter state — independent so switching levels doesn't lose context
  const [filtersByLevel, setFiltersByLevel] = useState<Record<Level, FilterState>>({
    1: makeEmptyFilters(1),
    2: makeEmptyFilters(2),
    3: makeEmptyFilters(3)
  });
  // Per-level visible columns
  const [chargeVisibleColumns, setChargeVisibleColumns] = useState<string[]>(CHARGE_COLUMNS.map(c => c.key));
  const [materialVisibleColumns, setMaterialVisibleColumns] = useState<string[]>(MATERIAL_COLUMNS.map(c => c.key));
  const currentFilters = filtersByLevel[level];
  const setCurrentFilters = (f: FilterState) => setFiltersByLevel(prev => ({
    ...prev,
    [level]: f
  }));
  // Helper: does a value match the search term?
  const matchSearch = (term: string, value: string) => !term || value.toLowerCase().includes(term.toLowerCase());
  // --- Level 1: filtered products ---
  const filteredProducts = useMemo(() => {
    const f = filtersByLevel[1];
    const term = f.search.trim();
    return products.filter(p => {
      if (term) {
        const matchesAnyField = f.searchFields.some(fieldKey => {
          if (fieldKey === 'Produktname') return matchSearch(term, p.name);
          if (fieldKey === 'Produktnummer') return matchSearch(term, p.productNumber);
          return false;
        });
        if (!matchesAnyField) return false;
      }
      if (f.status !== 'Alle' && !p.statuses.includes(f.status as any)) return false;
      if (f.dateFrom && !isAfterOrEqual(p.lastProductionDate, f.dateFrom)) return false;
      if (f.dateTo && !isBeforeOrEqual(p.lastProductionDate, f.dateTo)) return false;
      return true;
    });
  }, [filtersByLevel]);
  // --- Level 2: batches for product ---
  const productBatches = useMemo(() => {
    if (!selectedProduct) return [];
    const existing = batches.filter(b => b.productId === selectedProduct.id);
    const base = existing.length > 0 ? existing : Array.from({
      length: 3
    }).map((_, i) => ({
      id: `dummy-${selectedProduct.id}-${i}`,
      productId: selectedProduct.id,
      batchNumber: `CH-2026-05-${Math.floor(Math.random() * 900) + 100}`,
      productionDate: selectedProduct.lastProductionDate,
      status: i === 0 ? 'In Produktion' : 'Abgeschlossen'
    }) as Batch);
    const f = filtersByLevel[2];
    const term = f.search.trim();
    return base.filter(b => {
      if (term) {
        const matchesAnyField = f.searchFields.some(fieldKey => {
          if (fieldKey === 'Chargen-Nr.') return matchSearch(term, b.batchNumber);
          if (fieldKey === 'Produktionsdatum') return matchSearch(term, b.productionDate);
          return false;
        });
        if (!matchesAnyField) return false;
      }
      if (f.status !== 'Alle' && b.status !== f.status) return false;
      if (f.dateFrom && !isAfterOrEqual(b.productionDate, f.dateFrom)) return false;
      if (f.dateTo && !isBeforeOrEqual(b.productionDate, f.dateTo)) return false;
      return true;
    });
  }, [selectedProduct, filtersByLevel]);
  // --- Level 3: details ---
  const currentBatchDetails = useMemo(() => {
    if (!selectedBatch) return null;
    return batchDetails[selectedBatch.id] || batchDetails['b1'];
  }, [selectedBatch]);
  // Suggestions per level — distinct values for each searchable field
  const productSuggestions = useMemo(() => ({
    Produktname: uniqueSorted(products.map(p => p.name)),
    Produktnummer: uniqueSorted(products.map(p => p.productNumber))
  }), []);
  const chargeSuggestions = useMemo(() => {
    if (!selectedProduct) return {};
    const existing = batches.filter(b => b.productId === selectedProduct.id);
    return {
      'Chargen-Nr.': uniqueSorted(existing.map(b => b.batchNumber)),
      Produktionsdatum: uniqueSorted(existing.map(b => b.productionDate))
    };
  }, [selectedProduct]);
  const materialSuggestions = useMemo(() => {
    if (!selectedBatch) return {};
    const details = batchDetails[selectedBatch.id] || batchDetails['b1'];
    if (!details) return {};
    const all = details.sections.flatMap(s => s.materials);
    return {
      Material: uniqueSorted(all.map(m => m.name)),
      Lieferant: uniqueSorted(all.map(m => m.supplier)),
      'Chargen-Nr.': uniqueSorted(all.map(m => m.batchNo)),
      MHD: uniqueSorted(all.map(m => m.mhd))
    };
  }, [selectedBatch]);
  // Navigation
  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setLevel(2);
    setReturnToTransfer(false);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleSelectBatch = (batch: Batch) => {
    setSelectedBatch(batch);
    setLevel(3);
    setReturnToTransfer(false);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleBackToProductList = () => {
    setSelectedProduct(null);
    setSelectedBatch(null);
    setLevel(1);
    setReturnToTransfer(false);
  };
  const handleBackToBatchList = () => {
    setSelectedBatch(null);
    setLevel(2);
    setReturnToTransfer(false);
  };
  const handleTransfer = (portal: Portal, batchIds: string[], timestamp: string) => {
    setBatches(prev => prev.map(b => batchIds.includes(b.id) ? {
      ...b,
      transfers: {
        ...(b.transfers ?? {}),
        [portal]: {
          transferred: true,
          transferredAt: timestamp
        }
      }
    } : b));
  };
  // Only completed batches that still need to be transferred to at least one portal
  const pendingTransferCount = useMemo(() => {
    return batches.filter(b => b.status === 'Abgeschlossen' && (!b.transfers?.ATC?.transferred || !b.transfers?.OSapiens?.transferred)).length;
  }, [batches]);
  const filterBarProps = level === 1 ? {
    level,
    filters: currentFilters,
    onChange: setCurrentFilters,
    suggestions: productSuggestions,
    onOpenTransfer: () => setView('transfer'),
    pendingTransferCount
  } : level === 2 ? {
    level,
    filters: currentFilters,
    onChange: setCurrentFilters,
    columns: CHARGE_COLUMNS,
    visibleColumns: chargeVisibleColumns,
    onVisibleColumnsChange: setChargeVisibleColumns,
    suggestions: chargeSuggestions
  } : {
    level,
    filters: currentFilters,
    onChange: setCurrentFilters,
    columns: MATERIAL_COLUMNS,
    visibleColumns: materialVisibleColumns,
    onVisibleColumnsChange: setMaterialVisibleColumns,
    suggestions: materialSuggestions
  };
  if (view === 'transfer') {
    return <TransferPage batches={batches} products={products} onTransfer={handleTransfer} onBack={() => setView('main')} onOpenBatch={batch => {
      const product = products.find(p => p.id === batch.productId);
      if (!product) return;
      setSelectedProduct(product);
      setSelectedBatch(batch);
      setLevel(3);
      setView('main');
      setReturnToTransfer(true);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }} data-id="element-0" />;
  }
  return <div className="min-h-screen bg-[#F8FAFC] font-sans text-gray-900" data-id="element-1">
      {/* Context-aware Filter Bar */}
      <FilterBar {...filterBarProps} data-id="element-2" />

      <main className="relative overflow-hidden min-h-[calc(100vh-160px)]" data-id="element-3">
        <AnimatePresence mode="wait" data-id="element-4">
          {level === 1 && <ProductGrid key="level-1" products={filteredProducts} onSelectProduct={handleSelectProduct} data-id="element-5" />}

          {level === 2 && selectedProduct && <ChargeList key="level-2" product={selectedProduct} batches={productBatches} visibleColumns={chargeVisibleColumns} onBack={handleBackToProductList} onSelectBatch={handleSelectBatch} data-id="element-6" />}

          {level === 3 && selectedProduct && selectedBatch && <ChargeDetail key="level-3" product={selectedProduct} batch={selectedBatch} details={currentBatchDetails} filters={currentFilters} visibleColumns={materialVisibleColumns} onBackToProduct={handleBackToProductList} onBackToBatchList={handleBackToBatchList} returnToTransfer={returnToTransfer} onBackToTransfer={() => {
          setView('transfer');
          setReturnToTransfer(false);
        }} data-id="element-7" />}
        </AnimatePresence>
      </main>
    </div>;
}
// Helpers — parse dd.mm.yyyy and compare with ISO date strings
function parseDdMmYyyy(s: string): Date | null {
  const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!m) return null;
  return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
}
function isAfterOrEqual(ddmmyyyy: string, iso: string) {
  const d = parseDdMmYyyy(ddmmyyyy);
  if (!d) return true;
  return d >= new Date(iso);
}
function isBeforeOrEqual(ddmmyyyy: string, iso: string) {
  const d = parseDdMmYyyy(ddmmyyyy);
  if (!d) return true;
  return d <= new Date(iso);
}
function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values.filter(v => v && v !== '-'))).sort((a, b) => a.localeCompare(b, 'de'));
}